import { escapeXml, API_BASE, broadcastUserId, buildAuthXml, buildXmlPayload, sendXmlRequest } from './core';
import { LEGACY_UPDATE_MAP } from '../../constants/objects';

// ============ QUERY FUNCTIONS ============
export const fetchIntacctData = async (config, queryObj, fieldsString, sqlFilter, pageSize, offset = 0, showPrivate = false, orderBy = null) => {
  const response = await fetch(`${API_BASE}/api/query-v2`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...config, object: queryObj, fields: fieldsString, query: sqlFilter, pageSize: pageSize || 2000, offset, showPrivate, orderBy }) });
  const text = await response.text();
  let result;
  try { result = JSON.parse(text); } catch (e) { throw new Error(`Server Response Error: ${text.substring(0, 200)}...`); }
  if (!result || !result.success) { const errorDesc = result?.error || "Unknown Server Error"; throw new Error(errorDesc); }
  return result;
};

export const fetchMoreIntacctData = async (config, queryObj, fieldsString, sqlFilter, pageSize, offset, showPrivate = false, orderBy = null) => {
  return fetchIntacctData(config, queryObj, fieldsString, sqlFilter, pageSize, offset, showPrivate, orderBy);
};

export const fetchIntacctDataLegacy = async (config, queryObj, fieldsString, legacyQuery = '', pageSize = 1000, fetchAll = true) => {
  const response = await fetch(`${API_BASE}/api/read-by-query`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...config,
      object: queryObj,
      fields: fieldsString,
      query: legacyQuery,
      pageSize: Math.min(pageSize, 1000),
      fetchAll: fetchAll
    })
  });
  
  const text = await response.text();
  let result;
  try { result = JSON.parse(text); } catch (e) { throw new Error(`Server Response Error: ${text.substring(0, 200)}...`); }
  if (!result || !result.success) { const errorDesc = result?.error || "Unknown Server Error"; throw new Error(errorDesc); }
  return result;
};

// ============ INSPECT ============
export const inspectIntacctObject = async (config, objectName) => {
  const response = await fetch(`${API_BASE}/api/inspect`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...config, object: objectName }) });
  const result = await response.json();
  broadcastUserId(result);
  if (!result.success) throw new Error(result.error);
  return result.fields || [];
};

// ============ STANDARD WRITE HELPERS (shared by preview and write) ============

// Array fields that expand into repeating nested XML structures
const ARRAY_FIELD_MAP = {
  STANDARDTASK: {
    STANDARDCOSTTYPEID: {
      wrapper: 'STANDARDTASKSTANDARDCOSTTYPES',
      item: 'STANDARDTASKSTANDARDCOSTTYPE',
      field: 'STANDARDCOSTTYPEID'
    }
  }
};

const toXml = (obj) => {
  let xml = '';
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v)) {
      xml += `<${k}>`;
      for (const item of v) { xml += toXml(item); }
      xml += `</${k}>`;
    } else if (typeof v === 'object' && v !== null) {
      xml += `<${k}>${toXml(v)}</${k}>`;
    } else {
      const strVal = String(v);
      const xmlVal = strVal.trim().toLowerCase() === 'blank' ? '' : escapeXml(strVal);
      xml += `<${k}>${xmlVal}</${k}>`;
    }
  }
  return xml;
};

const buildXmlFromRecord = (objectName, record, mode) => {
  const nested = {};
  Object.entries(record).forEach(([key, val]) => {
    if (mode === 'create' && key === 'RECORDNO') return;
    const legacyConfig = LEGACY_UPDATE_MAP[objectName];
    if (mode === 'update' && legacyConfig && key === 'RECORDNO' && legacyConfig.key.toLowerCase() === 'recordno') return;
    if (mode === 'update' && legacyConfig && key.toLowerCase() === legacyConfig.key.toLowerCase()) return;
    // Check for array field expansion (comma/semicolon-separated → repeating XML)
    const arrayConfig = ARRAY_FIELD_MAP[objectName]?.[key];
    if (arrayConfig && val != null && String(val).trim() !== '') {
      const values = String(val).split(/[,;]/).map(v => v.trim()).filter(Boolean);
      if (values.length > 0) {
        nested[arrayConfig.wrapper] = values.map(v => ({
          [arrayConfig.item]: { [arrayConfig.field]: v }
        }));
      }
      return;
    }
    const parts = key.split('.');
    let current = nested;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (i === parts.length - 1) { current[part] = val; } else { current[part] = current[part] || {}; current = current[part]; }
    }
  });
  return toXml(nested);
};

export const buildWritePreviewXml = (objectName, records, mode) => {
  if (!records || records.length === 0) return '';
  const rec = records[0];
  const legacyConfig = LEGACY_UPDATE_MAP[objectName];
  const actionTag = mode === 'create' ? 'create' : 'update';
  let content = '';
  if (mode === 'update' && legacyConfig) {
    const keyField = Object.keys(rec).find(k => k.toLowerCase() === legacyConfig.key.toLowerCase());
    const keyValue = keyField ? rec[keyField] : rec['RECORDNO'];
    const keyAttr = ` ${legacyConfig.key}="${escapeXml(String(keyValue))}"`;
    content = `<${legacyConfig.function}${keyAttr}>${buildXmlFromRecord(objectName, rec, mode)}</${legacyConfig.function}>`;
  } else {
    content = `<${actionTag}><${objectName}>${buildXmlFromRecord(objectName, rec, mode)}</${objectName}></${actionTag}>`;
  }
  return `<function controlid="sample">\n  ${content}\n</function>`;
};

// ============ STANDARD WRITE (CREATE/UPDATE) ============
export const writeIntacctRecords = async (config, objectName, records, mode = 'update') => {
  if (!objectName) throw new Error("Error: Object Name is undefined.");
  
  const functions = records.map((rec, index) => {
    const legacyConfig = LEGACY_UPDATE_MAP[objectName];
    const controlIdValue = rec['RECORDNO'] || rec['VENDORID'] || rec['CUSTOMERID'] || `${new Date().getTime()}-${index}`;
    let content = '';
    if (mode === 'update' && legacyConfig) {
      const keyField = Object.keys(rec).find(k => k.toLowerCase() === legacyConfig.key.toLowerCase());
      const keyValue = keyField ? rec[keyField] : rec['RECORDNO'];
      const keyAttr = ` ${legacyConfig.key}="${escapeXml(String(keyValue))}"`;
      content = `<${legacyConfig.function}${keyAttr}>${buildXmlFromRecord(objectName, rec, mode)}</${legacyConfig.function}>`;
    } else {
      const actionTag = mode === 'update' ? 'update' : 'create';
      content = `<${actionTag}><${objectName}>${buildXmlFromRecord(objectName, rec, mode)}</${objectName}></${actionTag}>`;
    }
    return `<function controlid="${escapeXml(String(controlIdValue))}">${content}</function>`;
  }).join('');
  
  const xmlPayload = buildXmlPayload(config, functions);
  const result = await sendXmlRequest(config, objectName, xmlPayload);
  return result.results;
};

// ============ ATTACHMENTS ============
export const getDefaultSavePath = async () => { const res = await fetch(`${API_BASE}/api/browse/default-path`); const data = await res.json(); return data.success ? data.path : ''; };
export const browseFolderPath = async () => {
  const res = await fetch(`${API_BASE}/api/browse/folder`);
  const data = await res.json();
  return data.success && data.path ? data.path : null;
};
export const downloadAttachmentsBatch = async (config, attachmentIds, savePath) => {
  const response = await fetch(`${API_BASE}/api/attachment/batch`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...config, attachmentIds, savePath }) });
  const result = await response.json();
  broadcastUserId(result);
  if (!result.success) throw new Error(result.error || 'Download failed');
  return result;
};

// ============ GET MODE FUNCTIONS (read/readByName) ============
export const buildGetPreviewXml = (objectName, keys, keyType, config) => {
  const functionTag = keyType === 'recordno' ? 'read' : 'readByName';
  const sampleKeys = keys.slice(0, 3).join(',');
  const moreNote = keys.length > 3 ? `\n<!-- ... and ${keys.length - 3} more keys ... -->` : '';
  
  const authXml = buildAuthXml({ ...config, userPassword: '********', senderPassword: '********' });
  
  return `<?xml version="1.0" encoding="UTF-8"?>
<request>
  <control>
    <senderid>${config.senderId}</senderid>
    <password>********</password>
    <controlid>${new Date().getTime()}</controlid>
    <uniqueid>false</uniqueid>
    <dtdversion>3.0</dtdversion>
  </control>
  <operation>
    <authentication>
      ${config.useSessionId && config.sessionId 
        ? `<sessionid>${config.sessionId}</sessionid>` 
        : `<login>
        <userid>${config.userId}</userid>
        <companyid>${config.companyId}</companyid>
        <password>********</password>${config.locationId ? `
        <locationid>${config.locationId}</locationid>` : ''}
      </login>`}
    </authentication>
    <content>
      <function controlid="get-func">
        <${functionTag}>
          <object>${objectName}</object>
          <keys>${escapeXml(sampleKeys)}</keys>${moreNote}
          <fields>*</fields>
        </${functionTag}>
      </function>
    </content>
  </operation>
</request>`;
};

export const fetchIntacctDataByGet = async (config, objectName, keys, keyType = 'name') => {
  if (!keys || keys.length === 0) {
    throw new Error('No keys provided for GET operation');
  }
  
  const functionTag = keyType === 'recordno' ? 'read' : 'readByName';
  const BATCH_SIZE = 100;
  const allData = [];
  const errors = [];
  
  const extractRecords = (data, objName) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    
    const upperName = objName.toUpperCase();
    const lowerName = objName.toLowerCase();
    
    if (data[upperName]) return Array.isArray(data[upperName]) ? data[upperName] : [data[upperName]];
    if (data[lowerName]) return Array.isArray(data[lowerName]) ? data[lowerName] : [data[lowerName]];
    if (data[objName]) return Array.isArray(data[objName]) ? data[objName] : [data[objName]];
    
    if (data.RECORDNO !== undefined || data.recordno !== undefined) return [data];
    
    const dataKeys = Object.keys(data);
    const recordKey = dataKeys.find(k => 
      k !== 'listtype' && k !== 'count' && k !== '@' && 
      typeof data[k] === 'object'
    );
    if (recordKey && data[recordKey]) {
      return Array.isArray(data[recordKey]) ? data[recordKey] : [data[recordKey]];
    }
    
    return [];
  };
  
  const rawXmlRequests = [];
  const rawXmlResponses = [];
  
  for (let i = 0; i < keys.length; i += BATCH_SIZE) {
    const batchKeys = keys.slice(i, i + BATCH_SIZE);
    const keysString = batchKeys.join(',');
    
    const functionXml = `<function controlid="get-batch-${i}"><${functionTag}><object>${objectName}</object><keys>${escapeXml(keysString)}</keys><fields>*</fields></${functionTag}></function>`;
    
    const xmlPayload = buildXmlPayload(config, functionXml);
    rawXmlRequests.push(xmlPayload);
    console.log(`=== GET BATCH ${i / BATCH_SIZE + 1} XML ===\n`, xmlPayload, '\n=== END XML ===');
    
    const response = await fetch(`${API_BASE}/api/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...config, object: objectName, xmlrequest: xmlPayload, returnRawXml: true })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server returned ${response.status}: ${response.statusText}. Details: ${errorText}`);
    }
    
    const result = await response.json();
    broadcastUserId(result);
    
    if (result.rawXml) rawXmlResponses.push(result.rawXml);
    
    console.log('=== GET RESPONSE ===', JSON.stringify(result, null, 2), '\n=== END RESPONSE ===');
    
    let batchRecords = [];
    
    if (result.data && Array.isArray(result.data)) {
      batchRecords = result.data;
    } else if (result.data && typeof result.data === 'object') {
      batchRecords = extractRecords(result.data, objectName);
    } else if (result.results && Array.isArray(result.results)) {
      result.results.forEach(res => {
        if (res.status === 'success' && res.data) {
          const records = extractRecords(res.data, objectName);
          batchRecords.push(...records);
        } else if (res.status !== 'success') {
          errors.push(res.errormessage || 'Unknown error');
        }
      });
    }
    
    allData.push(...batchRecords);
  }
  
  return {
    success: true,
    data: allData,
    metadata: {
      totalRequested: keys.length,
      totalReturned: allData.length,
      batches: Math.ceil(keys.length / BATCH_SIZE),
      errors: errors.length > 0 ? errors : null
    },
    rawXml: {
      requests: rawXmlRequests,
      responses: rawXmlResponses
    }
  };
};
